import { initializeDB } from '../config/db';
import { URL, WITNESS_STORE } from '../config/constants';

/**
 * Generate UUID v4
 * @returns {string}
 */
function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

/**
 * Service for managing witness statements
 */
const witnessService = {
    /**
     * Get witness form data
     * @param {string} incidentId - Incident ID
     * @param {string} witnessId - Witness ID
     * @returns {Promise<Object|null>}
     */
    async getWitnessForm(incidentId, witnessId) {
        try {
            const db = await initializeDB();
            
            // Create witness store if it doesn't exist
            if (!db.objectStoreNames.contains(WITNESS_STORE)) {
                return null;
            }
            
            const tx = db.transaction(WITNESS_STORE, 'readonly');
            const store = tx.objectStore(WITNESS_STORE);
            
            // Look for witness form by composite key
            const key = `${incidentId}_${witnessId}`;
            const witnessForm = await store.get(key);
            
            return witnessForm || null;
        } catch (error) {
            console.error('Error getting witness form:', error);
            return null;
        }
    },
    
    /**
     * Save witness form data
     * @param {string} incidentId - Incident ID
     * @param {string} witnessId - Witness ID
     * @param {Object} formData - Form data to save
     * @returns {Promise<void>}
     */
    async saveWitnessForm(incidentId, witnessId, formData) {
        try {
            const db = await initializeDB();
            
            // Check if witness store exists
            if (!db.objectStoreNames.contains(WITNESS_STORE)) {
                console.error('Witness store does not exist. Database upgrade needed.');
                return;
            }
            
            // Use existing witness store
            const tx = db.transaction(WITNESS_STORE, 'readwrite');
            const store = tx.objectStore(WITNESS_STORE);
            
            const key = `${incidentId}_${witnessId}`;
            const witnessRecord = {
                id: key,
                incidentId: incidentId,
                witnessId: witnessId,
                uuid: generateUUID(),
                statement: formData,
                status: formData.status || 'draft', // Use status from formData if provided
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };
            
            // If formData contains submittedAt, add it to the record
            if (formData.submittedAt) {
                witnessRecord.submittedAt = formData.submittedAt;
            }
            
            // Check if record exists
            const existing = await store.get(key);
            if (existing) {
                witnessRecord.createdAt = existing.createdAt;
                witnessRecord.uuid = existing.uuid;
                // Preserve submitted status if already submitted
                if (existing.status === 'submitted') {
                    witnessRecord.status = 'submitted';
                    witnessRecord.submittedAt = existing.submittedAt;
                }
            }
            
            await store.put(witnessRecord);
        } catch (error) {
            console.error('Error saving witness form:', error);
            throw error;
        }
    },
    
    /**
     * Submit witness form
     * @param {string} incidentId - Incident ID
     * @param {string} witnessId - Witness ID
     * @param {Object} formData - Form data to submit
     * @returns {Promise<void>}
     */
    async submitWitnessForm(incidentId, witnessId, formData) {
        try {
            // Save locally first with submitted status
            await this.saveWitnessForm(incidentId, witnessId, {
                ...formData,
                status: 'submitted',
                submittedAt: new Date().toISOString()
            });
            
            // Try to submit to API if online
            if (navigator.onLine) {
                // Extract witness index from witnessId (e.g., "witness-0" -> 0)
                const witnessIndex = parseInt(witnessId.replace('witness-', ''), 10);
                
                const token = localStorage.getItem('token');
                const response = await fetch(`${URL}/incidents/${incidentId}/witness`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        witnessIndex: witnessIndex,  // Send the witness index
                        witnessInfo: {
                            name: formData.witnessName || '',
                            phone: formData.witnessPhone || '',
                            email: formData.witnessEmail || ''
                        },
                        formData: {
                            attentionDrawn: formData.whatDrewAttention || '',
                            sawIncident: formData.didSeeIncident || false,
                            exactLocation: formData.exactLocation || '',
                            witnessLocation: formData.whereStanding || '',
                            timeOfIncident: formData.timeOfIncident || '',
                            areaAwayFrom: formData.howFarAway || '',
                            personInvolved: formData.wereFacing ? 'Yes' : 'No',
                            completeDetails: formData.completeDetails || '',
                            signature: formData.signature || null
                        }
                    })
                });
                
                const result = await response.json();
                
                if (response.ok && result.success) {
                    // Update local status to submitted
                    const db = await initializeDB();
                    if (db.objectStoreNames.contains(WITNESS_STORE)) {
                        const tx = db.transaction(WITNESS_STORE, 'readwrite');
                        const store = tx.objectStore(WITNESS_STORE);
                        const key = `${incidentId}_${witnessId}`;
                        const record = await store.get(key);
                        if (record) {
                            record.status = 'submitted';
                            record.submittedAt = new Date().toISOString();
                            record.serverId = result.id; // Store server ID if returned
                            await store.put(record);
                        }
                    }
                    return result;
                } else {
                    throw new Error(result.message || 'Failed to submit to API');
                }
            } else {
                // Queue for offline sync
                const db = await initializeDB();
                if (db.objectStoreNames.contains(WITNESS_STORE)) {
                    const tx = db.transaction(WITNESS_STORE, 'readwrite');
                    const store = tx.objectStore(WITNESS_STORE);
                    const key = `${incidentId}_${witnessId}`;
                    const record = await store.get(key);
                    if (record) {
                        record.status = 'pending';
                        await store.put(record);
                    }
                }
            }
        } catch (error) {
            console.error('Error submitting witness form:', error);
            throw error;
        }
    },
    
    /**
     * Sync pending witness statements
     * @returns {Promise<void>}
     */
    async syncPendingStatements() {
        try {
            const db = await initializeDB();
            if (!db.objectStoreNames.contains(WITNESS_STORE)) {
                return;
            }
            
            const tx = db.transaction(WITNESS_STORE, 'readonly');
            const store = tx.objectStore(WITNESS_STORE);
            const statusIndex = store.index('status');
            const pendingStatements = await statusIndex.getAll('pending');
            
            for (const statement of pendingStatements) {
                try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${URL}/incidents/${statement.incidentId}/witness-statements`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            witnessId: statement.witnessId,
                            statement: statement.statement
                        })
                    });
                    
                    if (response.ok) {
                        // Update status to submitted
                        const writeTx = db.transaction(WITNESS_STORE, 'readwrite');
                        const writeStore = writeTx.objectStore(WITNESS_STORE);
                        statement.status = 'submitted';
                        statement.submittedAt = new Date().toISOString();
                        await writeStore.put(statement);
                    }
                } catch (error) {
                    console.error('Error syncing witness statement:', statement.id, error);
                }
            }
        } catch (error) {
            console.error('Error in syncPendingStatements:', error);
        }
    }
};

export default witnessService;